import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from '@/components/ui/dialog.tsx'
import { Button } from '@/components/ui/button.tsx'
import { DialogTitle } from '@radix-ui/react-dialog'
import { Address, AddressType, UspsAddress } from '@/@types/generated'
import { Label } from '../ui/label'
import format from '@/lib/format'

export type AddressValidationDialogProps = {
  enteredAddress: Address & { type: AddressType }
  suggestedAddress?: UspsAddress
  open: boolean
  onOpenChange?: (open: boolean) => void
  onBack: () => void
  onUseSuggested?: () => void
  onContinueAnyway?: () => void
}

export function AddressValidationDialog({
  enteredAddress,
  suggestedAddress,
  open,
  onOpenChange,
  onBack,
  onUseSuggested,
  onContinueAnyway,
}: AddressValidationDialogProps) {
  const addressLabel = format.Enum(AddressType, enteredAddress.type)

  if (suggestedAddress) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent variant="warning">
          <DialogHeader>
            <DialogTitle>Address Correction Suggested</DialogTitle>
            <DialogDescription>
              USPS has a suggested correction for the{' '}
              {addressLabel.toLowerCase()} address you entered.
            </DialogDescription>
            <DialogDescription>
              Please select which address to use:
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 items-start gap-4">
            <Label>Entered Address</Label>
            <Label>Suggested Address</Label>

            {/* Entered */}
            <address className="text-sm">
              {enteredAddress.street1 ?? '\u2014'}
              <br />
              {enteredAddress.street2 && (
                <>
                  {enteredAddress.street2}
                  <br />
                </>
              )}
              {enteredAddress.city ?? '\u2014'},{' '}
              {enteredAddress.stateProvince ?? '\u2014'}{' '}
              {enteredAddress.postalCode ?? '\u2014'}
            </address>

            {/* Suggested */}
            <address className="text-sm">
              {suggestedAddress.streetAddress ?? '\u2014'}
              <br />
              {suggestedAddress.secondaryAddress && (
                <>
                  {suggestedAddress.secondaryAddress}
                  <br />
                </>
              )}
              {suggestedAddress.city ?? '\u2014'},{' '}
              {suggestedAddress.state ?? '\u2014'}{' '}
              {suggestedAddress.zipCode ?? '\u2014'}
              {suggestedAddress.zipPlus4 ? `-${suggestedAddress.zipPlus4}` : ''}
            </address>

            <Button
              type="button"
              variant="outline"
              className="mt-2"
              onClick={onContinueAnyway}
            >
              Continue Anyway
            </Button>

            <Button type="button" className="mt-2" onClick={onUseSuggested}>
              Use Suggested
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="warning">
        <DialogHeader>
          <DialogTitle>{addressLabel} Address Not Found in USPS</DialogTitle>
          <DialogDescription>
            USPS indicates that the {addressLabel.toLowerCase()} address entered
            is not valid. Please ensure the entered address is correct and
            continue, or go back and correct the address.
          </DialogDescription>
        </DialogHeader>

        <div>
          <Label className="mb-2">Entered Address</Label>
          <address className="text-sm">
            {enteredAddress.street1 ?? '\u2014'}
            <br />
            {enteredAddress.street2 && (
              <>
                {enteredAddress.street2}
                <br />
              </>
            )}
            {enteredAddress.city ?? '\u2014'},{' '}
            {enteredAddress.stateProvince ?? '\u2014'}{' '}
            {enteredAddress.postalCode ?? '\u2014'}
          </address>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" onClick={onBack}>
              Go Back
            </Button>
          </DialogClose>
          <Button type="button" onClick={onContinueAnyway}>
            Continue Anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
